// Adapters between the real operator data model and the flat "lite" shapes the
// /supplier pages + gap engine consume. The pages were written against a hardcoded
// mock (see mockData.js). Rather than rewrite every page, we keep their flat
// view-model and translate to/from the real nested API payloads here.
//
//   company        ↔ operator            (GET/POST/PUT /operators)
//   installations  ↔ operator installations (…/installations/operator/*)
//   emissionFactors↔ versioned supplier factor list (GET /supplier/factors) —
//                    see supplierFactorReadModel.js (the single canonical
//                    mapping, B1); this file no longer derives it from the
//                    legacy installationDocs[].emissionsFactors arrays.
//   requests       ↔ connection requests (/requests, /requests/operator)
//
// S4: this file no longer owns a `share` mapping — the S8 Supplier Page
// publication settings are consumed directly (publicationSettings →
// MockDataContext's `share`, and SharePortalPage.jsx's own
// settingsToShareFormValues) rather than through an adapter here. The old
// portalToShare (cbam-portal status → lite share view) is removed along with
// the retired /operator/cbam-portal/* wiring it adapted (D-S9-3).
//
// Free-tier rules preserved: we never read or write SEFA / premium calc — those
// stay upsells. The read side drops any `sefa` on the record; the write side
// never sends one.

const str = (v) => (v == null ? '' : String(v));

// Country round-trips as whatever the operator record already holds. Real
// onboarding writes a numeric country id; the lite selects use ISO codes. Send
// a number when the value is purely numeric, otherwise pass the string through
// untouched so we never corrupt an existing numeric id into NaN.
export const toCountryPayload = (v) => {
  if (v === '' || v == null) return undefined;
  return /^\d+$/.test(String(v)) ? parseInt(v, 10) : v;
};

// A nested {_id}/{name} populated ref, or a raw scalar — normalise to a scalar.
const scalarOf = (v, keys = ['_id', 'id']) => {
  if (v && typeof v === 'object') {
    for (const k of keys) if (v[k] != null) return String(v[k]);
    return '';
  }
  return v == null ? '' : String(v);
};

const toDateStr = (v) => {
  if (!v) return '';
  const s = String(v);
  // Already YYYY-MM-DD or a full ISO string → take the date part.
  return s.length >= 10 ? s.slice(0, 10) : s;
};

/* ------------------------------------------------------------------ company */

// operator (GET /operators → .operator) → lite company view-model.
export function operatorToCompany(operator) {
  const addr = operator?.address || {};
  return {
    name: str(operator?.name),
    corporateRegistrationNumber: str(operator?.corporateRegistrationNumber),
    country: addr.country != null ? scalarOf(addr.country) : '',
    city: str(addr.city),
    contactName: str(operator?.contactName),
    contactEmail: str(operator?.contactEmail),
    phoneNumber: str(operator?.phoneNumber),
    // Not part of the operator model — display-only on the portal/preview.
    sector: operator?.sector || 'Iron & Steel',
    // The checklist/gap engine treat "saved" as "a persisted operator exists".
    saved: !!operator?._id,
  };
}

// lite company draft → PUT/POST /operators payload. Spreads the existing
// operator first (mirrors OperatorInfoForm) so fields the lite form doesn't
// surface (state, street, number, faradaiClient, …) are never wiped.
export function companyToOperatorPayload(company, existingOperator) {
  return {
    ...(existingOperator || {}),
    name: company.name,
    address: {
      ...(existingOperator?.address || {}),
      country: toCountryPayload(company.country),
      city: company.city,
    },
    contactName: company.contactName,
    contactEmail: company.contactEmail,
    phoneNumber: company.phoneNumber,
    corporateRegistrationNumber: company.corporateRegistrationNumber || null,
  };
}

/* ------------------------------------------------------------- installations */

// Full installation doc (GET /installations/operator/:id) → lite view-model.
export function installationToLite(inst) {
  const addr = inst?.address || {};
  return {
    id: str(inst?._id),
    name: str(inst?.name),
    internalId: str(inst?.internalId),
    cbamRegistryId: str(inst?.cbamRegistryId),
    country: addr.country != null ? scalarOf(addr.country, ['_id', 'id']) : '',
    city: str(addr.city),
    street: str(addr.street),
    postcode: str(addr.postcode),
    POBox: str(addr.POBox),
    unlocode: str(addr.UN_LOCODE),
    producerName: str(inst?.distributor),
    latitude: str(addr.latitude),
    longitude: str(addr.longitude),
  };
}

// The install(-ation) doc list (useMockData().installationDocs) → a display
// name for an id. Shared by every page that labels a factor/draft/upload row
// with its installation (EmissionFactorsPage, TemplateUploadPage,
// DraftsInboxPage, DraftReviewPage).
export const installationNameById = (installationDocs, id) =>
  (installationDocs || []).find((d) => String(d._id) === String(id))?.name || '—';

// lite installation form → POST /installations/operator/add | PUT payload.
// Mirrors CreateEditModal.payloadFromValues (nested address, `distributor` for
// "Name of Producer", `UN_LOCODE`). Preserves an existing authorizedRep.
export function liteToInstallationPayload(form, existing) {
  return {
    name: form.name,
    internalId: form.internalId || undefined,
    cbamRegistryId: form.cbamRegistryId || undefined,
    distributor: form.producerName || undefined,
    address: {
      country: toCountryPayload(form.country),
      city: form.city,
      street: form.street,
      POBox: form.POBox,
      postcode: form.postcode,
      UN_LOCODE: form.unlocode || undefined,
      latitude: form.latitude || undefined,
      longitude: form.longitude || undefined,
    },
    ...(existing?.authorizedRep ? { authorizedRep: existing.authorizedRep } : {}),
  };
}

/* ----------------------------------------------------------------- requests */

const REQUEST_STATUS_MAP = {
  pending: 'pending',
  accepted: 'accepted',
  sent: 'pending',
  declined: 'declined',
  rejected: 'declined',
};

// A connection-request item (/requests or /requests/operator) → lite request
// card. The request record now carries `goods` (CN codes) + `reportingPeriod`
// (see P4), so the card renders them instead of empty chips.
export function requestToLite(item, { direction, type }) {
  const party = item.declarantId || item.senderId || item.recieverId || {};
  const country = item.country || party?.address?.country || {};
  return {
    id: str(item._id),
    direction,
    company: party?.name || item.recieverName || country?.name || '—',
    type,
    goods: Array.isArray(item.goods) ? item.goods : [],
    period: item.reportingPeriod || item.period || '',
    message: item.message || '',
    status: REQUEST_STATUS_MAP[item.status] || item.status || 'pending',
    date: toDateStr(item.createdAt || item.updatedAt),
  };
}
